import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const UseCasesSection = () => {
  const [activeUseCase, setActiveUseCase] = useState('customer-support');

  const useCases = {
    'customer-support': {
      title: 'Customer Support',
      description: 'Build exceptional customer support experiences with real-time chat, ticket management, and agent collaboration tools.',
      image: 'https://images.unsplash.com/photo-1553484771-371a605b060b?w=600&h=400&fit=crop',
      features: [
        'Live chat widgets',
        'Agent dashboards',
        'Ticket routing',
        'Chat history',
        'File sharing',
        'Canned responses'
      ],
      metrics: {
        'Response Time': '< 2 seconds',
        'Customer Satisfaction': '98%',
        'Agent Productivity': '+45%'
      },
      technologies: ['WebSocket', 'REST API', 'Push Notifications'],
      clientExample: 'TechFlow Inc reduced response times by 60% and improved customer satisfaction scores.'
    },
    'team-collaboration': {
      title: 'Team Collaboration',
      description: 'Create powerful team communication platforms with channels, direct messaging, and real-time collaboration features.',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop',
      features: [
        'Team channels',
        'Direct messaging',
        'File sharing',
        'Voice/video calls',
        'Screen sharing',
        'Integration hub'
      ],
      metrics: {
        'Team Productivity': '+35%',
        'Message Delivery': '99.9%',
        'User Engagement': '+50%'
      },
      technologies: ['WebRTC', 'WebSocket', 'File Upload API'],
      clientExample: 'StartupXYZ built their entire team communication platform in just 2 weeks.'
    },
    'gaming': {
      title: 'Gaming & Entertainment',
      description: 'Power multiplayer games and entertainment platforms with ultra-low latency messaging and real-time interactions.',
      image: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=600&h=400&fit=crop',
      features: [
        'Real-time gameplay',
        'Player chat',
        'Guild systems',
        'Leaderboards',
        'In-game notifications',
        'Matchmaking'
      ],
      metrics: {
        'Latency': '< 50ms',
        'Concurrent Players': '1M+',
        'Uptime': '99.95%'
      },
      technologies: ['WebSocket', 'UDP Protocol', 'Global CDN'],
      clientExample: 'GameDev Studios increased player engagement by 40% with real-time messaging features.'
    },
    'healthcare': {
      title: 'Healthcare & Telemedicine',
      description: 'Build secure healthcare communication platforms with HIPAA compliance and encrypted messaging for patient care.',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=600&h=400&fit=crop',
      features: [
        'Patient messaging',
        'Doctor consultations',
        'Secure file sharing',
        'Appointment scheduling',
        'Emergency alerts',
        'Compliance reporting'
      ],
      metrics: {
        'Security Compliance': 'HIPAA',
        'Data Encryption': 'End-to-end',
        'Audit Trail': '100%'
      },
      technologies: ['End-to-end Encryption', 'HIPAA Compliance', 'Audit Logs'],
      clientExample: 'Enterprise Solutions Corp deployed a HIPAA-compliant patient communication system.'
    },
    'fintech': {
      title: 'Financial Services',
      description: 'Create secure financial communication platforms with real-time trading alerts, customer support, and compliance features.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop',
      features: [
        'Trading alerts',
        'Customer support',
        'Transaction notifications',
        'Compliance messaging',
        'Secure document sharing',
        'Multi-factor auth'
      ],
      metrics: {
        'Security Standard': 'SOC 2 Type II',
        'Transaction Speed': '< 100ms',
        'Compliance': '100%'
      },
      technologies: ['SOC 2 Compliance', 'Real-time APIs', 'Encryption'],
      clientExample: 'FinTech Innovations built a secure trading platform with 99.9% uptime guarantee.'
    },
    'ecommerce': {
      title: 'E-commerce & Retail',
      description: 'Enhance shopping experiences with live chat support, order notifications, and customer engagement tools.',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop',
      features: [
        'Live shopping assistance',
        'Order notifications',
        'Inventory alerts',
        'Customer reviews',
        'Abandoned cart recovery',
        'Loyalty programs'
      ],
      metrics: {
        'Conversion Rate': '+25%',
        'Customer Retention': '+30%',
        'Support Efficiency': '+40%'
      },
      technologies: ['Webhooks', 'Push Notifications', 'Analytics API'],
      clientExample: 'RetailCorp increased conversion rates by 25% with live chat integration.'
    }
  };

  const useCaseCategories = [
    { id: 'customer-support', label: 'Customer Support', icon: 'Headphones' },
    { id: 'team-collaboration', label: 'Team Collaboration', icon: 'Users' },
    { id: 'gaming', label: 'Gaming', icon: 'Gamepad2' },
    { id: 'healthcare', label: 'Healthcare', icon: 'Heart' },
    { id: 'fintech', label: 'FinTech', icon: 'TrendingUp' },
    { id: 'ecommerce', label: 'E-commerce', icon: 'ShoppingCart' }
  ];

  const currentUseCase = useCases?.[activeUseCase];

  return (
    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-secondary/10 border border-secondary/20 rounded-full px-4 py-2 mb-6">
            <Icon name="Layers" size={16} className="text-secondary" />
            <span className="text-sm font-medium text-secondary">
              Use Cases
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Built for every
            <span className="block text-secondary">industry</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            From startups to enterprises, see how MessagingHub Pro powers communication 
            across different industries and use cases.
          </p>
        </div>

        {/* Use Case Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {useCaseCategories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setActiveUseCase(category?.id)}
              className={`inline-flex items-center space-x-2 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeUseCase === category?.id
                  ? 'bg-primary text-primary-foreground shadow-elevation-2'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              <Icon name={category?.icon} size={18} />
              <span>{category?.label}</span>
            </button>
          ))}
        </div>

        {/* Use Case Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1">
            <h3 className="text-3xl font-bold text-foreground mb-4">
              {currentUseCase?.title}
            </h3>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              {currentUseCase?.description}
            </p>

            {/* Features */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-foreground mb-4">
                Key Features
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {currentUseCase?.features?.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-accent flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Metrics */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-foreground mb-4">
                Performance Metrics
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {Object.entries(currentUseCase?.metrics)?.map(([key, value]) => (
                  <div key={key} className="bg-card border border-border rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-primary mb-1">{value}</div>
                    <div className="text-xs text-muted-foreground">{key}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Technologies */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-foreground mb-4">
                Technologies Used
              </h4>
              <div className="flex flex-wrap gap-2">
                {currentUseCase?.technologies?.map((tech, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-muted/50 text-muted-foreground rounded-full text-sm"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* Client Example */}
            <div className="bg-card/50 border border-border rounded-lg p-6 mb-8">
              <div className="flex items-start space-x-3">
                <Icon name="Quote" size={20} className="text-primary mt-1 flex-shrink-0" />
                <p className="text-muted-foreground italic">
                  {currentUseCase?.clientExample}
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => window.location.href = '/api-documentation'}
                className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-200"
              >
                <Icon name="FileText" size={18} />
                <span>View Documentation</span>
              </button>
              <button
                onClick={() => window.location.href = '/contact-sales'}
                className="inline-flex items-center justify-center space-x-2 px-6 py-3 border border-border rounded-lg text-foreground hover:bg-muted/50 transition-colors duration-200"
              >
                <Icon name="MessageSquare" size={18} />
                <span>Discuss Your Use Case</span>
              </button>
            </div>
          </div>

          {/* Image */}
          <div className="order-1 lg:order-2">
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-elevation-3">
                <Image
                  src={currentUseCase?.image}
                  alt={currentUseCase?.title}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Overlay Badge */}
              <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-sm border border-border rounded-lg px-3 py-2">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium text-foreground">
                    Live Demo Available
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="mt-20 text-center">
          <div className="bg-card border border-border rounded-xl p-8">
            <h3 className="text-2xl font-bold text-foreground mb-8">
              Powering communication across industries
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <div className="text-3xl font-bold text-primary mb-2">10,000+</div>
                <div className="text-sm text-muted-foreground">Active Developers</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-secondary mb-2">1B+</div>
                <div className="text-sm text-muted-foreground">Messages Delivered</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-accent mb-2">99.9%</div>
                <div className="text-sm text-muted-foreground">Uptime SLA</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-warning mb-2">15+</div>
                <div className="text-sm text-muted-foreground">Global Regions</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;